import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Download, Loader2, CreditCard, ChevronLeft, ChevronRight, Search, Filter } from "lucide-react";
import { StudentBilling } from "@/hooks/useFinanceData";
import { PaymentRegistration } from "./PaymentRegistration";
import { useExportData } from "@/hooks/useExportData";
import { useToast } from "@/hooks/use-toast";

interface RecentInvoicesTableProps {
  billing: StudentBilling[];
  billingLoading: boolean;
  loadingBillId: string | null;
  onMarkAsPaid: (billId: string) => void;
  onMarkAllAsPaid?: () => void;
  refetchBilling?: () => void;
}

export const RecentInvoicesTable = ({ 
  billing, 
  billingLoading, 
  loadingBillId, 
  onMarkAsPaid,
  onMarkAllAsPaid,
  refetchBilling 
}: RecentInvoicesTableProps) => {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedBillForPayment, setSelectedBillForPayment] = useState<StudentBilling | null>(null);
  const { exportInvoices, isExporting } = useExportData();
  const { toast } = useToast();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("");

  const filteredBilling = billing.filter(bill => {
    const matchesSearch = searchTerm === '' || 
      bill.student?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.student?.class.toLowerCase().includes(searchTerm.toLowerCase()) ||
      bill.activity?.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
    
    const matchesDate = dateFilter === '' || 
      bill.invoice_date.includes(dateFilter) ||
      bill.due_date.includes(dateFilter);
    
    return matchesSearch && matchesStatus && matchesDate;
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  const totalPages = Math.ceil(filteredBilling.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentBilling = filteredBilling.slice(startIndex, endIndex);

  const handlePaymentSuccess = async () => {
    try {
      console.log('Payment success handler called');
      
      setShowPaymentModal(false);
      setSelectedBillForPayment(null);
      
      toast({
        title: "Pago registrado",
        description: "El pago se ha registrado correctamente",
      });
      
      if (refetchBilling) {
        console.log('Refreshing billing data...');
        await refetchBilling();
      }
      
    } catch (error) {
      console.error('Error in payment success handler:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: 'Error al actualizar los datos después del pago'
      });
    }
  };

  const handleExport = () => {
    exportInvoices(billing, `facturas_${new Date().toISOString().split('T')[0]}`);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (value: string) => {
    setItemsPerPage(parseInt(value));
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    setCurrentPage(1);
  };

  return (
    <Card className="shadow-card">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Facturas Recientes
          </CardTitle>
          <div className="flex gap-2">
            {onMarkAllAsPaid && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onMarkAllAsPaid}
                disabled={loadingBillId === 'all' || billingLoading || filteredBilling.filter(b => b.status === 'pending').length === 0}
                className="bg-success hover:bg-success/90 text-success-foreground border-success"
              >
                {loadingBillId === 'all' ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                {loadingBillId === 'all' ? 'Procesando...' : 'Marcar Todas como Pagadas'}
              </Button>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleExport}
              disabled={isExporting || billingLoading || billing.length === 0}
            >
              {isExporting ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Download className="w-4 h-4 mr-2" />
              )}
              {isExporting ? 'Exportando...' : 'Exportar'}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="space-y-2">
            <label className="text-sm font-medium">Buscar</label>
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Estudiante, clase o actividad..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Estado</label>
            <Select value={statusFilter} onValueChange={handleStatusFilterChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="pending">Pendiente</SelectItem>
                <SelectItem value="paid">Pagado</SelectItem>
                <SelectItem value="overdue">Vencido</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <label className="text-sm font-medium">Fecha (YYYY-MM)</label>
            <Input
              type="month"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              placeholder="Filtrar por mes"
            />
          </div>
        </div>

        {billingLoading ? (
          <div className="text-center py-8 text-muted-foreground">
            Cargando facturas...
          </div>
        ) : billing.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No hay facturas generadas
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">Estudiante</th>
                  <th className="text-left py-2 px-3 font-medium">Actividad</th>
                  <th className="text-left py-2 px-3 font-medium">Tipo</th>
                  <th className="text-left py-2 px-3 font-medium">Sesiones</th>
                  <th className="text-left py-2 px-3 font-medium">Importe</th>
                  <th className="text-left py-2 px-3 font-medium">Estado</th>
                  <th className="text-left py-2 px-3 font-medium">Vencimiento</th>
                  <th className="text-left py-2 px-3 font-medium">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {currentBilling.map((bill) => (
                  <tr key={bill.id} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-3">
                      <div>
                        <p className="font-medium">{bill.student?.name}</p>
                        <p className="text-sm text-muted-foreground">{bill.student?.class}</p>
                      </div>
                    </td>
                    <td className="py-2 px-3">{bill.activity?.name}</td>
                    <td className="py-2 px-3">
                      <Badge 
                        variant="outline" 
                        className={bill.billing_type === 'per_session' ? 'border-blue-500 text-blue-700' : 'border-purple-500 text-purple-700'}
                      >
                        {bill.billing_type === 'per_session' ? 'Por Día' : 'Mensual'}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-center">{bill.sessions_attended}</td>
                    <td className="py-2 px-3 font-medium">{bill.total_amount.toFixed(2)}€</td>
                    <td className="py-2 px-3">
                      <Badge 
                        variant={
                          bill.status === 'paid' ? 'default' :
                          bill.status === 'pending' ? 'secondary' :
                          bill.status === 'overdue' ? 'destructive' : 'outline'
                        }
                      >
                        {bill.status === 'paid' ? 'Pagado' :
                         bill.status === 'pending' ? 'Pendiente' :
                         bill.status === 'overdue' ? 'Vencido' : 'Cancelado'}
                      </Badge>
                    </td>
                    <td className="py-2 px-3 text-sm">
                      {new Date(bill.due_date).toLocaleDateString()}
                    </td>
                    <td className="py-2 px-3">
                      {bill.status === 'pending' ? (
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedBillForPayment(bill);
                              setShowPaymentModal(true);
                            }}
                            className="bg-success hover:bg-success/90 text-success-foreground h-8 px-3"
                          >
                            <CreditCard className="w-3 h-3 mr-1" />
                            Registrar Pago
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => onMarkAsPaid(bill.id)}
                            disabled={loadingBillId === bill.id}
                            className="h-8 px-3"
                          >
                            {loadingBillId === bill.id ? (
                              <>
                                <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                Procesando...
                              </>
                            ) : (
                              'Marcar como Pagada'
                            )}
                          </Button>
                        </div>
                      ) : bill.status === 'paid' ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="bg-success hover:bg-success/90 text-success-foreground border-success h-8 px-3 cursor-default"
                          disabled
                        >
                          Pagada
                        </Button>
                      ) : (
                        <Badge variant="secondary">
                          {bill.status === 'overdue' ? 'Vencida' : 'Cancelada'}
                        </Badge>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {filteredBilling.length > 0 && (
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                Mostrando {startIndex + 1} a {Math.min(endIndex, filteredBilling.length)} de {filteredBilling.length} facturas
                {billing.length !== filteredBilling.length && ` (${billing.length} totales)`}
              </span>
              <Select value={itemsPerPage.toString()} onValueChange={handleItemsPerPageChange}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
              >
                <ChevronLeft className="w-4 h-4" />
                Anterior
              </Button>
              
              <span className="text-sm text-muted-foreground">
                Página {currentPage} de {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
              >
                Siguiente
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}
      </CardContent>
      
      {selectedBillForPayment && (
        <PaymentRegistration
          bill={selectedBillForPayment}
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedBillForPayment(null);
          }}
          onSuccess={handlePaymentSuccess}
        />
      )}
    </Card>
  );
};
